import { useState } from "react";
import './model-tree-in-model-space.css';
import { ModelNameHandler } from "../model-name-handler/model-name-handler";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { ModelInModelTree } from "../../model-space/model-space";

export const hasSelectedChild = (model: ModelInModelTree, selectedUUID: string): boolean => {
    if (model.children) {
        for (const childModel of model.children) {
            if (childModel.uuid === selectedUUID) return true;

            const _hasSelectedChild = hasSelectedChild(childModel, selectedUUID);
            if (_hasSelectedChild) return true;
        }
    }
    return false;
};

type ModelTreeItemProps = {
    model: ModelInModelTree;
    selectedUUID: string | null;
    onSelect: (uuid: string) => void;
    onSelectWithDoubleClick?: (uuid: string) => void;
    isDataChangeable?: boolean;
    onModelNameChange?: (uuid: string, name: string) => void;
    onVisibleChange?: (uuid: string, visible: boolean) => void;
};

const ModelTreeItem = ({ model, selectedUUID, onSelect, onModelNameChange, onVisibleChange, isDataChangeable = true, onSelectWithDoubleClick }: ModelTreeItemProps) => {
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const [prevSelectedUUID, setPrevSelectedUUID] = useState<typeof selectedUUID>(null);

    if (prevSelectedUUID !== selectedUUID) {
        setPrevSelectedUUID(selectedUUID);
        if (selectedUUID !== null && hasSelectedChild(model, selectedUUID)) {
            setIsSubMenuOpen(true);
        }
    }

    return (
        <li>
            {
                model?.children && model.children.length > 0 && (
                    <span
                        style={{
                            color: isSubMenuOpen ? 'red' : 'green',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                        }}
                        onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
                    >
                        { isSubMenuOpen ? '-' : '+' }&nbsp;
                    </span>
                )
            }
            {
                isDataChangeable === true && (
                    <>
                        <span style={{ cursor: 'pointer' }} onClick={() => { if (onVisibleChange) onVisibleChange(model.uuid, !model.visible); }}>
                            { model.visible ? <EyeInvisibleOutlined /> : <EyeOutlined /> }
                        </span>
                        &nbsp;
                    </>
                )
            }
            {
                isDataChangeable === true ? (
                    <span
                        className={`name ${ selectedUUID === model.uuid ? 'active' : '' }`}
                        onClick={() => onSelect(model.uuid)}
                        onDoubleClick={() => { if (onSelectWithDoubleClick) onSelectWithDoubleClick(model.uuid); }}
                    >
                        <ModelNameHandler defaultValue={model.name} onSave={(name) => { if (onModelNameChange) onModelNameChange(model.uuid, name) }} />
                    </span>
                ) : (
                    <span
                        className={`name ${ selectedUUID === model.uuid ? 'active' : '' }`}
                        onClick={() => onSelect(model.uuid)}
                    >
                        { model.name }
                    </span>
                )
            }
            {
                isSubMenuOpen && model?.children && model.children.length > 0 && (
                    <ul>
                        {
                            model.children.map(_model => (
                                <ModelTreeItem
                                    model={_model}
                                    selectedUUID={selectedUUID}
                                    onSelect={onSelect}
                                    onSelectWithDoubleClick={onSelectWithDoubleClick}
                                    key={_model.uuid}
                                    isDataChangeable={isDataChangeable}
                                    onModelNameChange={onModelNameChange}
                                    onVisibleChange={onVisibleChange}
                                />
                            ))
                        }
                    </ul>
                )
            }
        </li>
    );
};

type ModelTreeInModelSpaceProps = {
    models: ModelInModelTree[];
    onSelect: (uuid: string) => void;
    onSelectWithDoubleClick?: (uuid: string) => void;
    selectedUUID: string | null;
    isDataChangeable?: boolean;
    onModelNameChange?: (uuid: string, name: string) => void;
    onVisibleChange?: (uuid: string, visible: boolean) => void;
};

export const ModelTreeInModelSpace = ({ models, onSelect, selectedUUID, onModelNameChange, onVisibleChange, isDataChangeable = true, onSelectWithDoubleClick }: ModelTreeInModelSpaceProps) => {
    return (
        <div className='model-tree-in-model-space'>
            <ul>
                {
                    models.map(model => (
                        <ModelTreeItem
                            key={model.uuid}
                            model={model}
                            selectedUUID={selectedUUID}
                            onSelect={onSelect}
                            onSelectWithDoubleClick={onSelectWithDoubleClick}
                            isDataChangeable={isDataChangeable}
                            onModelNameChange={onModelNameChange}
                            onVisibleChange={onVisibleChange}
                        />
                    ))
                }
            </ul>
        </div>
    );
};